import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    FormControlLabel,
    FormGroup
} from '@mui/material';
import { isEmpty, isString } from 'lodash';
import moment from 'moment';
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import 'jspdf';
import React, { ChangeEvent, FC, memo, useMemo, useState } from 'react';
import { Tooltip, Button } from '@mui/material';
import { faCog } from '@fortawesome/fontawesome-free-solid';
import ModalImage from '../../../ModalImage';
import styles from './Inventory.module.scss';
import { naturalSort } from '../../../../utils/sorting';
import { Fa } from '../../../elements/fa';

type Order = 'asc' | 'desc';

export interface IInventoryDevicesInfo {
    id: string;
    name: string;
    photoUrl: string | null;
    lastPingAddress: string | null;
    lastPingStreet?: string;
    lastPingCity?: string;
    lastPingState?: string;
    lastPingZip?: string;
    make: string;
    lastReportDate: string;
    lastTripDate: string;
    type: string;
    assignee: string;
    [key: string]: any;
}

declare module 'jspdf' {
    interface jsPDF {
        autoTable: (options: any) => jsPDF;
    }
}

interface ColumnDefinition {
    id: string;
    label: string;
}

interface Props {
    devices: IInventoryDevicesInfo[];
    availableExtraColumns?: ColumnDefinition[];
}

const photoColumnDef: ColumnDefinition = { id: 'photoUrl', label: 'Photo' };

const defaultHeadCells: ColumnDefinition[] = [
    { id: 'name', label: 'Vehicle' },
    { id: 'assignee', label: 'Assignee' },
    { id: 'make', label: 'Year Make Model' },
    { id: 'type', label: 'Type' },
    { id: 'lastPingAddress', label: 'Current Location' },
    { id: 'lastReportDate', label: 'Last Report' },
];

const DevicesTable: FC<Props> = memo(({ devices, availableExtraColumns = [] }) => {
    const [order, setOrder] = useState<Order>('asc');
    const [orderBy, setOrderBy] = useState<string | null>('name');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [clickedImage, setClickedImage] = useState<{ open: boolean, img: string } | null>(null);
    const [showColumnModal, setShowColumnModal] = useState(false);
    const [selectedExtraIds, setSelectedExtraIds] = useState<string[]>(['photoUrl']);

    const ALLOWED_SORT_FIELDS = [
        'name',
        'lastPingAddress',
        'lastReportDate',
        'lastTripDate',
    ];

    const visibleHeadCells = useMemo(() => {
        const selectedExtras = availableExtraColumns.filter(c => selectedExtraIds.includes(c.id));
        const isPhotoSelected = selectedExtraIds.includes(photoColumnDef.id);

        return [
            ...(isPhotoSelected ? [photoColumnDef] : []),
            ...defaultHeadCells,
            ...selectedExtras
        ];
    }, [selectedExtraIds, availableExtraColumns]);

    const availableColumns = useMemo(() => {
        const ignoredLabels = [
            'year', 'make', 'model', 'type', 'assignee',
            'vehicle', 'current location', 'last report',
        ];

        const defaultLabels = defaultHeadCells.map(c => c.label.toLowerCase());
        const allIgnored = new Set([...ignoredLabels, ...defaultLabels, photoColumnDef]);

        const filteredExtras = availableExtraColumns.filter(c => !allIgnored.has(c.label.toLowerCase()));

        return [photoColumnDef, ...filteredExtras];
    }, [availableExtraColumns]);

    const handleRequestSort = (headCellId: string) => {
        if (!ALLOWED_SORT_FIELDS.includes(headCellId) && !selectedExtraIds.includes(headCellId)) return;

        const isSameField = orderBy === headCellId;
        if (isSameField) {
            if (order === 'asc') {
                setOrder('desc');
            } else {
                setOrderBy(null);
                setOrder('asc');
            }
        } else {
            setOrderBy(headCellId);
            setOrder('asc');
        }
        // Reset pagination on sort change
        setPage(0);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleToggleColumn = (id: string) => {
        setSelectedExtraIds(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    async function processImage(url, maxDimension = 100, format = "png", quality = 0.92) {
        try {
            const apiBaseUrl = process.env.REACT_APP_API_URL;
            if(!url) return "";

            const encodedUrl = encodeURIComponent(url);
            const proxyUrl = `${apiBaseUrl}/api/image/proxy?url=${encodedUrl}`;
            const res = await fetch(proxyUrl, { mode: "cors" });
            if (!res.ok) throw new Error("Failed to load image");

            const blob = await res.blob();
            const bitmap = await createImageBitmap(blob);

            let scale = 1;
            if (bitmap.width > bitmap.height && bitmap.width > maxDimension) {
                scale = maxDimension / bitmap.width;
            } else if (bitmap.height > bitmap.width && bitmap.height > maxDimension) {
                scale = maxDimension / bitmap.height;
            } else if (bitmap.width === bitmap.height && bitmap.width > maxDimension) {
                scale = maxDimension / bitmap.width;
            }

            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            canvas.width = bitmap.width * scale;
            canvas.height = bitmap.height * scale;

            ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

            const mimeType = `image/${format.toLowerCase()}`;
            return canvas.toDataURL(mimeType, quality);
        } catch (e) {
            console.error(e);
            return "";
        }
    }

    const sortedDevices = useMemo(() => {
        const sliced = orderBy
            ? [...devices].sort(getComparator(order, orderBy))
            : devices;

        return sliced.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [devices, order, orderBy, page, rowsPerPage]);

    const getCellValue = (device: IInventoryDevicesInfo, colId: string): string => {
        if (colId in device) {
            const val = device[colId as keyof IInventoryDevicesInfo];
            if (colId === 'apportioned') return val ? 'Yes' : 'No';
            if (colId === 'photoUrl') return '';
            if (val === null || val === undefined) return '';
            if (typeof val === 'boolean') return val ? 'Yes' : 'No';
            return String(val);
        }

        const extraInfo = (device as any)['extra-info'] || (device as any).extraInfo;

        if (extraInfo && extraInfo[colId]) {
            const val = extraInfo[colId];

            if (typeof val === 'object' && val !== null) {
                if (val.text) return val.text;
                if (val.date) return val.date;
                if (val.boolean !== undefined) return val.boolean ? 'Yes' : 'No';
                return '';
            }

            return String(val);
        }

        return '';
    };

    async function exportDevicesToPDF(devicesToExport: IInventoryDevicesInfo[], columns: ColumnDefinition[]) {
        const doc = new jsPDF();

        const headerLabels = columns.map(c => c.label);

        const rows = [];
        for (const d of devicesToExport) {
            const rowData: any[] = [];

            for (const col of columns) {
                if (col.id === 'photoUrl') {
                    const photo = d.photoUrl ? await processImage(d.photoUrl) : "";
                    rowData.push({ content: "", image: photo });
                } else {
                    rowData.push(getCellValue(d, col.id));
                }
            }
            rows.push(rowData);
        }

        doc.autoTable({
            head: [headerLabels],
            body: rows,
            startY: 20,
            styles: { fontSize: 8 },
            didParseCell: (data) => {
                if (data.cell.raw && (data.cell.raw as any).image) {
                    data.cell.height = 40;
                }
            },
            didDrawCell: (data) => {
                if (data.cell.raw && (data.cell.raw as any).image) {
                    const imgProps = doc.getImageProperties((data.cell.raw as any).image);
                    const cellWidth = data.cell.width - 1;
                    const cellHeight = data.cell.height - 1;
                    let imgWidth = cellWidth;
                    let imgHeight = (imgProps.height * imgWidth) / imgProps.width;

                    if (imgHeight > cellHeight) {
                        imgHeight = cellHeight;
                        imgWidth = (imgProps.width * imgHeight) / imgProps.height;
                    }

                    const x = data.cell.x + (cellWidth - imgWidth) / 2;
                    const y = data.cell.y + (cellHeight - imgHeight) / 2;

                    doc.addImage(
                        (data.cell.raw as any).image,
                        "JPEG",
                        x,
                        y,
                        imgWidth,
                        imgHeight
                    );
                }
            }
        });

        doc.save("devices.pdf");
    }

    async function exportDevicesToExcel(devicesToExport: IInventoryDevicesInfo[], columns: ColumnDefinition[]) {
        const sorted = [...devicesToExport].sort((a, b) =>
            a.name.localeCompare(b.name, undefined, {
                numeric: true,
                sensitivity: 'base',
            })
        );

        const excelColumns = columns.reduce<ColumnDefinition[]>((acc, col) => {
            if (col.id === 'lastPingAddress') {
                acc.push(
                    { id: 'lastPingStreet', label: 'Address Street' },
                    { id: 'lastPingCity', label: 'Address City' },
                    { id: 'lastPingState', label: 'Address State' },
                    { id: 'lastPingZip', label: 'Address Zip' },
                );
                return acc;
            }

            acc.push(col);
            return acc;
        }, []);

        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet('Devices');

        ws.columns = excelColumns.map(col => ({
            header: col.label,
            key: col.id,
            width: col.id === 'photoUrl' ? 11 : 20
        }));

        const photoColIndex = excelColumns.findIndex(c => c.id === 'photoUrl');

        for (const d of sorted) {
            const rowData: any = {};
            excelColumns.forEach(col => {
                if(col.id !== 'photoUrl') {
                    rowData[col.id] = getCellValue(d, col.id);
                }
            });

            const row = ws.addRow(rowData);

            ws.columns.forEach((col) => {
                col.eachCell({ includeEmpty: true }, (cell) => {
                    cell.font = { name: 'Calibri', size: 8 };
                    cell.alignment = { vertical: 'middle', horizontal: 'center' };
                });
            });

            row.height = photoColIndex !== -1 ? 50 : 20;

            if (photoColIndex !== -1 && d.photoUrl) {
                const imageData = await processImage(d.photoUrl, 50);
                if (imageData) {
                    const imageId = wb.addImage({
                        base64: imageData as string,
                        extension: 'png',
                    });

                    ws.addImage(imageId, {
                        tl: { col: photoColIndex, row: row.number - 1 },
                        ext: { width: 80, height: 45 },
                        editAs: 'oneCell',
                    });
                }
            }
        }

        wb.xlsx.writeBuffer().then((buffer) => {
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            saveAs(blob, 'Inventor Report.xlsx');
        });
    }

    return (
        <div className={styles.inventoryPaper}>
            <div className={styles.inventoryExport}>
               <span>
                    <Tooltip title="Export as PDF">
                        <Button onClick={() => exportDevicesToPDF(devices, visibleHeadCells)}>PDF <img alt="Download on PDF" src={'/assets/fa-svgs/solid/file-pdf.svg'} /></Button>
                    </Tooltip>
               </span>
               <span>
                   <Tooltip title="Export as XLSX">
                       <Button onClick={() => exportDevicesToExcel(devices, visibleHeadCells)}>XLSX <img alt="Download on PDF" src={'/assets/fa-svgs/solid/file-excel.svg'} /></Button>
                   </Tooltip>
               </span>
               <span>
                   <Tooltip title="Table Settings">
                       <span
                           className={styles.pickerText}
                           role="button"
                           tabIndex={0}
                           onClick={() => setShowColumnModal(true)}
                           onKeyDown={(e) => {
                               if (e.key === 'Enter' || e.key === ' ') {
                                   e.preventDefault();
                                   setShowColumnModal(true);
                               }
                           }}
                       >
                           Picker <Fa icon={faCog} />
                       </span>
                   </Tooltip>
               </span>
            </div>

            <TableContainer className={styles.inventoryTableContainer}>
                <Table>
                    <TableHead className={styles.inventoryHeader}>
                        <TableRow className={styles.inventoryHeaderRow}>
                            {visibleHeadCells.map((headCell) => (
                                <TableCell
                                    key={headCell.id}
                                    sortDirection={orderBy === headCell.id ? order : false}
                                    className={styles.inventoryHeaderCell}
                                    style={{ fontWeight: 300 }}
                                >
                                    {ALLOWED_SORT_FIELDS.includes(headCell.id) ? (
                                        <TableSortLabel
                                            active={orderBy === headCell.id}
                                            direction={orderBy === headCell.id ? order : 'asc'}
                                            onClick={() => handleRequestSort(headCell.id)}
                                        >
                                            {headCell.label}
                                        </TableSortLabel>
                                    ) : (
                                        headCell.label
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {sortedDevices.map((device) => (
                            <TableRow key={device.id}>
                                {visibleHeadCells.map(col => {
                                    if (col.id === 'photoUrl') {
                                        return (
                                            <TableCell key={col.id} style={{ fontWeight: 500 }}>
                                                <img
                                                    src={device.photoUrl || 'images/no_car_image.jpg'}
                                                    alt={device.name}
                                                    className={styles.deviceImage}
                                                    width={83}
                                                    height={48}
                                                    onClick={() => setClickedImage({ open: true, img: device.photoUrl || '' })}
                                                />
                                            </TableCell>
                                        );
                                    }
                                    return (
                                        <TableCell key={col.id} style={{ fontWeight: 500 }}>
                                            {getCellValue(device, col.id)}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                className={styles.inventoryPagination}
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={devices.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
            {clickedImage?.open && (
                <ModalImage
                    closeImageHandler={() => setClickedImage(null)}
                    imageSrc={clickedImage.img ? clickedImage.img : 'images/no_car_image.jpg'}
                />
            )}

            <Dialog open={showColumnModal} onClose={() => setShowColumnModal(false)}>
                <DialogTitle className={styles.customDialogTitle}>Select Extra Columns</DialogTitle>
                <DialogContent>
                    {availableColumns.length === 0 ? (
                        <div>No extra columns available.</div>
                    ) : (
                        <FormGroup>
                            {availableColumns.map(col => (
                                <FormControlLabel
                                    key={col.id}
                                    style={{ margin: 0 }}
                                    control={
                                        <Checkbox
                                            checked={selectedExtraIds.includes(col.id)}
                                            onChange={() => handleToggleColumn(col.id)}
                                            name={col.id}
                                            style={{ padding: 0 }}
                                            size="small"
                                        />
                                    }
                                    label={
                                        <span style={{ fontSize: 11 }}>
                                            {col.label}
                                        </span>
                                    }
                                />
                            ))}
                        </FormGroup>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowColumnModal(false)} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
});

export default DevicesTable;

function getComparator<T, Key extends keyof T>(
    order: Order,
    orderBy: Key
): (a: T, b: T) => number {
    return (a, b) => customComparator(a, b, order, orderBy);
}

function customComparator<T>(
    a: T,
    b: T,
    order: Order,
    orderBy: keyof T
): number {
    const getValue = (obj: any, key: string) => {
        if (key in obj) return obj[key];
        const extra = obj['extra-info'] || obj.extraInfo;
        if (extra && extra[key]) {
             const val = extra[key];
             if (typeof val === 'object') return val.text || val.date || '';
             return val;
        }
        return '';
    };

    const aVal = getValue(a, orderBy as string);
    const bVal = getValue(b, orderBy as string);

    const isDateField = ['lastReportDate', 'lastTripDate'].includes(orderBy as string);

    // DATE FIELDS
    if (isDateField && aVal && bVal) {
        const aDate = moment(aVal, 'DD/MM/YYYY', true);
        const bDate = moment(bVal, 'DD/MM/YYYY', true);

        if (!aDate.isValid() && !bDate.isValid()) return 0;
        if (!aDate.isValid()) return 1;
        if (!bDate.isValid()) return -1;

        return (order === 'asc' ? 1 : -1) * (aDate.valueOf() - bDate.valueOf());
    }

    if (orderBy === 'name') {
        return naturalSort(a, b, order);
    }

    // STRING FIELDS
    if (isString(aVal) && isString(bVal)) {
        const aTrim = aVal.trim();
        const bTrim = bVal.trim();

        const aEmpty = isEmpty(aTrim);
        const bEmpty = isEmpty(bTrim);

        if (aEmpty && bEmpty) return 0;
        if (aEmpty) return 1;
        if (bEmpty) return -1;

        return (order === 'asc' ? 1 : -1) * aTrim.localeCompare(bTrim);
    }

    return 0;
}